const {
  formatMoney,
  isANumber,
  parseInteger,
  buildProperties,
} = window.BAO.Utils

class ProductDetailSets extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['variant', 'button']
  }

  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.handleFormSubmission = this.handleFormSubmission.bind(this)
    this.onQuantityUpdate = this.onQuantityUpdate.bind(this)
    this.onWindowScroll = this.onWindowScroll.bind(this)
    this.onMainFormStateChange = this.onMainFormStateChange.bind(this)

    const buttons = [
      ...(this.els?.button?.elements || []),
      ...(this.externalEls?.buttonCta?.elements || []),
    ]

    if (buttons.length > 0 && this.els?.form?.exists) {
      buttons.forEach(button => {
        this.listeners.add(button, 'click', this.handleFormSubmission)
      })
    }

    const quantityElement = this.externalEls?.quantity?.element
    this.quantityObserver?.disconnect()
    this.quantityObserver = new MutationObserver(() => {
      this.calculateTotalQuantity()
      this.calculateSubtotal()
      this.updateButtonStates()
    })

    if (quantityElement) {
      this.quantityObserver.observe(quantityElement, {
        attributes: true,
      })
    }

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.onQuantityUpdate,
    )

    this.setupFooterObserver()

    this.calculateTotalQuantity()
    this.calculateSubtotal()
    this.updateButtonStates()

    this.headerObserver = new MutationObserver(() => {
      this.setupFooterObserver()
    })
    this.headerObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-header-visible'],
    })

    const mainFormEl = this.externalEls?.form?.element
    this.mainFormObserver?.disconnect()
    if (mainFormEl) {
      this.mainFormObserver = new MutationObserver(this.onMainFormStateChange)
      this.mainFormObserver.observe(mainFormEl, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    }
  }

  onMainFormStateChange () {
    this.updateButtonStates()
    this.evaluateFooterVisibility()
  }

  setupFooterObserver () {
    this.footerObserver?.disconnect()
    window.removeEventListener('scroll', this.onWindowScroll)

    const footerEl = this.els?.footer?.element
    const ctaButtons = this.externalEls?.buttonCta?.elements || []
    const externalButtons = [
      ...ctaButtons,
      ...(this.externalEls?.buttonWishlist?.elements || []),
    ]

    if (!footerEl) return

    if (externalButtons.length === 0) {
      footerEl.setAttribute(
        'aria-active',
        this.isMainProductFormInlineHidden() ? 'false' : 'true',
      )
      return
    }

    const headerOffset =
      getComputedStyle(document.body)
        .getPropertyValue('--Sticky_Header_Visible_Offset')
        .trim() || '0px'
    this.headerOffsetPx = parseFloat(headerOffset) || 0
    this.ctaButtonSet = new Set(ctaButtons)

    this.buttonIntersecting = new Map()
    this.buttonVisibility = new Map()
    externalButtons.forEach(btn => {
      const rect = btn.getBoundingClientRect()
      const isIntersecting =
        rect.bottom > this.headerOffsetPx && rect.top < window.innerHeight
      this.buttonIntersecting.set(btn, isIntersecting)
      this.buttonVisibility.set(btn, isIntersecting)
    })
    this.ctaEarlyHidden = new Map()
    this.lastScrollY = window.scrollY

    this.footerObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          this.buttonIntersecting.set(entry.target, entry.isIntersecting)
        })
        this.evaluateFooterVisibility()
      },
      { threshold: 0, rootMargin: `-${headerOffset} 0px 0px 0px` },
    )

    externalButtons.forEach(btn => this.footerObserver.observe(btn))
    this.footerEl = footerEl
    window.addEventListener('scroll', this.onWindowScroll, { passive: true })

    const isMainFormInlineHidden = this.isMainProductFormInlineHidden()
    this.footerEl.setAttribute(
      'data-out-of-stock',
      isMainFormInlineHidden
        ? 'true'
        : this.totalQuantity > 0
          ? 'false'
          : String(this.isGenuinelySoldOut()),
    )
    this.evaluateFooterVisibility()
  }

  onWindowScroll () {
    this.evaluateFooterVisibility()
  }

  evaluateFooterVisibility () {
    if (!this.footerEl || !this.buttonIntersecting || !this.buttonVisibility) {
      return
    }

    if (this.isMainProductFormInlineHidden()) {
      this.footerEl.setAttribute('aria-active', 'false')
      return
    }

    if (this.footerEl.getAttribute('data-out-of-stock') === 'true') {
      this.footerEl.setAttribute('aria-active', 'false')
      return
    }

    const CTA_EARLY_TRIGGER_PX = 20
    const CTA_EARLY_RELEASE_PX = 28

    this.buttonIntersecting.forEach((isIntersecting, buttonEl) => {
      let isVisible = isIntersecting

      if (isVisible && this.ctaButtonSet?.has(buttonEl)) {
        const rect = buttonEl.getBoundingClientRect()
        const visibleHeight = Math.max(
          0,
          Math.min(rect.bottom, window.innerHeight) -
            Math.max(rect.top, this.headerOffsetPx || 0),
        )

        const wasEarlyHidden = this.ctaEarlyHidden.get(buttonEl) === true
        const isTopClipped = rect.top <= (this.headerOffsetPx || 0)
        const shouldEnterEarlyHide =
          isTopClipped &&
          visibleHeight > 0 &&
          visibleHeight <= CTA_EARLY_TRIGGER_PX
        const shouldExitEarlyHide =
          !isTopClipped ||
          visibleHeight <= 0 ||
          visibleHeight > CTA_EARLY_RELEASE_PX
        const shouldEarlyHide = wasEarlyHidden
          ? !shouldExitEarlyHide
          : shouldEnterEarlyHide

        this.ctaEarlyHidden.set(buttonEl, shouldEarlyHide)

        if (shouldEarlyHide) {
          isVisible = false
        }
      } else if (this.ctaButtonSet?.has(buttonEl)) {
        this.ctaEarlyHidden.set(buttonEl, false)
      }

      this.buttonVisibility.set(buttonEl, isVisible)
    })

    const ctaButtons = this.externalEls?.buttonCta?.elements || []
    const anyCtaVisible = ctaButtons.some(buttonEl => {
      return this.buttonVisibility.get(buttonEl)
    })
    const anyVisible = [...this.buttonVisibility.values()].some(Boolean)
    const shouldShowFooter =
      ctaButtons.length > 0 ? !anyCtaVisible : !anyVisible
    const nextState = shouldShowFooter ? 'true' : 'false'

    this.footerEl.setAttribute('aria-active', nextState)
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.removeEventListener('scroll', this.onWindowScroll)
    this.quantityObserver?.disconnect()
    this.footerObserver?.disconnect()
    this.headerObserver?.disconnect()
    this.mainFormObserver?.disconnect()
  }

  isMainProductFormInlineHidden () {
    const mainFormEl = this.externalEls?.form?.element
    if (!mainFormEl) return false

    return mainFormEl.style?.display === 'none'
  }

  isBaseProductSoldOut () {
    const formEl = this.externalEls?.form?.element
    if (!formEl) return true
    return formEl.getAttribute('data-product-available') === 'false'
  }

  isGenuinelySoldOut () {
    const allSetsSoldOut = this.els.variant.elements.every(variantEl => {
      return variantEl.classList.contains('prd-DetailForm_Variant-unavailable')
    })
    return allSetsSoldOut && this.isBaseProductSoldOut()
  }

  updateButtonStates () {
    const ctaButtons = this.externalEls?.buttonCta?.elements || []
    const wishlistButtons = this.externalEls?.buttonWishlist?.elements || []

    const hasItems = this.subtotal > 0
    const soldOut = this.isGenuinelySoldOut()
    const isMainFormInlineHidden = this.isMainProductFormInlineHidden()

    if (wishlistButtons.length > 0) {
      if (soldOut && !hasItems) {
        ctaButtons.forEach(el => el.setAttribute('aria-hidden', 'true'))
        wishlistButtons.forEach(el => el.setAttribute('aria-hidden', 'false'))
      } else {
        ctaButtons.forEach(el => el.setAttribute('aria-hidden', 'false'))
        wishlistButtons.forEach(el => el.setAttribute('aria-hidden', 'true'))
      }
    }

    if (this.footerEl) {
      if (isMainFormInlineHidden) {
        this.footerEl.setAttribute('data-out-of-stock', 'true')
      } else if (this.totalQuantity > 0) {
        this.footerEl.setAttribute('data-out-of-stock', 'false')
      } else {
        this.footerEl.setAttribute('data-out-of-stock', String(soldOut))
      }
    }
  }

  /**
   * Read a value from a form by input name which includes disabled inputs
   * - unlike FormData which excludes them.
   *
   * @param {HTMLFormElement} formEl
   * @param {string} name
   * @returns {string|null}
   */
  _getFormValue (formEl, name) {
    const el = formEl.elements.namedItem(name)
    return el?.value ?? null
  }

  /**
   * @param {Event} event
   *
   * @returns {void}
   */
  onQuantityUpdate (event) {
    const variantEl = event.target.closest(this.els?.variant?.selector)
    if (!isANumber(event?.detail?.value) || !variantEl) return

    variantEl.setAttribute('data-quantity', String(event.detail.value))

    const buttons = [
      ...(this.els?.button?.elements || []),
      ...(this.externalEls?.buttonCta?.elements || []),
    ]

    buttons.forEach(button => {
      button.setAttribute('aria-invalid', 'false')
    })

    this.calculateTotalQuantity()
    this.calculateSubtotal()
    this.updateButtonStates()
    this.evaluateFooterVisibility()
  }

  /**
   * @returns {void}
   */
  handleFormSubmission (e) {
    e.preventDefault()
    this.calculateTotalQuantity()

    const isValid = this.totalQuantity > 0

    const buttons = [
      ...(this.els?.button?.elements || []),
      ...(this.externalEls?.buttonCta?.elements || []),
    ]

    buttons.forEach(button => {
      button.setAttribute('aria-invalid', String(!isValid))
    })

    if (!isValid) return

    const itemsArray = this.els.form.elements
      .map(formEl => {
        const formData = new FormData(formEl)
        const id = parseInteger(formData.get('id'))
        const quantity = parseInteger(formData.get('quantity'))
        if (!id || !quantity) return null
        return {
          id,
          quantity,
          properties: buildProperties(formData),
        }
      })
      .filter(item => item)

    if (this.externalEls?.form?.element) {
      const formEl = this.externalEls.form.element
      const baseProduct = {
        id: parseInteger(this._getFormValue(formEl, 'id')),
        quantity: parseInteger(this._getFormValue(formEl, 'quantity')) || 0,
        properties: buildProperties(new FormData(formEl)),
      }
      itemsArray.push(baseProduct)
    }

    window.BAO.Cart.addItem(itemsArray)
  }

  calculateTotalQuantity () {
    this.totalQuantity = this.els.variant.elements
      .map(variantEl => {
        return parseInteger(variantEl?.dataset?.quantity) || 0
      })
      .reduce((previousValue, currentValue) => {
        return previousValue + currentValue
      }, 0)

    if (this.externalEls?.form?.element) {
      const formEl = this.externalEls.form.element
      const baseProductQuantity =
        parseInteger(this._getFormValue(formEl, 'quantity')) || 0

      this.totalQuantity += baseProductQuantity
    }
  }

  calculateSubtotal () {
    this.subtotal = this.els.variant.elements
      .map(variantEl => {
        return (
          (parseInteger(variantEl?.dataset?.price) || 0) *
          (parseInteger(variantEl?.dataset?.quantity) || 0)
        )
      })
      .reduce((previousValue, currentValue) => {
        return previousValue + currentValue
      }, 0)

    if (this.externalEls?.form?.element) {
      const formEl = this.externalEls.form.element
      const baseProductQuantity =
        parseInteger(this._getFormValue(formEl, 'quantity')) || 0
      const baseProductPrice = parseInteger(
        this.externalEls.formdata.element?.dataset?.price,
      )

      this.subtotal += baseProductPrice * baseProductQuantity
    }
  }

  /**
   * @returns {number} quantity - The current running total quantity selected with the associated quantity-adjusters
   */
  get totalQuantity () {
    return this._totalQuantity || 0
  }

  /**
   * @param {number} quantity - The current running total quantity selected with the associated quantity-adjusters
   *
   * @returns {undefined}
   */
  set totalQuantity (quantity) {
    this._totalQuantity = quantity
  }

  /**
   * @returns {number} subtotal - The current running subotal
   */
  get subtotal () {
    return this._subtotal || 0
  }

  /**
   * @param {number} subtotal - The current running subotal
   *
   * @returns {undefined}
   */
  set subtotal (subtotal) {
    this._subtotal = subtotal

    const subtotalEls = [
      ...(this.els?.subtotal?.elements || []),
      ...(this.externalEls?.subtotal?.elements || []),
    ]

    if (subtotalEls.length > 0) {
      subtotalEls.forEach(subtotalEl => {
        subtotalEl.textContent = `(${formatMoney(subtotal)})`
        subtotalEl.setAttribute('aria-hidden', String(subtotal < 1))
      })
    }
  }
}

if (!customElements.get('product-detail-sets')) {
  customElements.define('product-detail-sets', ProductDetailSets)
}
